import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { ArrowLeft, Lock, ShieldAlert, Eye } from "lucide-react";
import { toast } from "sonner";

const STATUS = {
  nueva: { label: "Nueva", cls: "bg-red-50 text-red-700" },
  en_revision: { label: "En revisión", cls: "bg-amber-50 text-amber-700" },
  en_seguimiento: { label: "En seguimiento", cls: "bg-blue-50 text-blue-700" },
  cerrada: { label: "Cerrada", cls: "bg-gray-100 text-gray-600" },
};
const NEXT = { nueva: ["en_revision"], en_revision: ["en_seguimiento", "cerrada"], en_seguimiento: ["en_revision", "cerrada"], cerrada: ["en_seguimiento"] };
const CATEGORY = { autolesion: "Autolesión", acoso: "Acoso", depresion: "Depresión/Ansiedad", familia: "Situación familiar", abuso: "Abuso", sustancias: "Sustancias", otros: "Situación sensible", contenido_inapropiado_tutor: "Respuesta del tutor bloqueada" };
const SITUATION = { proteccion: "Protección", autolesion: "Riesgo de autolesión", medica: "Posible emergencia médica", peligro: "Posible peligro inmediato" };
const fmt = (d) => (d ? new Date(d).toLocaleString("es-SV", { dateStyle: "medium", timeStyle: "short" }) : "—");
const detail = (e, m) => toast.error(e.response?.data?.detail || m);

const IncidentRow = ({ inc, active, onOpen }) => (
  <button onClick={() => onOpen(inc.id)} data-testid={`incident-row-${inc.id}`}
    className={`w-full text-left rounded-xl border p-3 transition-colors ${active ? "border-[#419cca] bg-blue-50/50" : "border-gray-100 bg-white hover:bg-gray-50"}`}>
    <div className="flex items-center justify-between gap-2">
      <span className="font-medium text-sm text-gray-900">{inc.student_name} · grado {inc.grade}</span>
      <Badge className={STATUS[inc.incident_status]?.cls}>{STATUS[inc.incident_status]?.label}</Badge>
    </div>
    <div className="flex items-center justify-between text-xs text-gray-500 mt-1">
      <span>{CATEGORY[inc.category] || inc.category} · prioridad {inc.severity === "alta" ? "alta" : "media"} (orientativa)</span>
      <span>{fmt(inc.created_at)}</span>
    </div>
    <div className="text-[11px] text-gray-400 mt-1">#{inc.id.slice(0, 8)} · {inc.human_reviewed_at ? "Revisada por una persona" : "Registrada · sin revisión humana"}</div>
  </button>
);

const Detail = ({ inc, onChanged }) => {
  const [note, setNote] = useState("");
  const setStatus = async (status) => {
    try {
      await axios.post(`${API}/incidents/${inc.id}/status`, { status, note }, { withCredentials: true });
      setNote(""); toast.success(`Estado: ${STATUS[status].label}`); onChanged();
    } catch (e) { detail(e, "No se pudo cambiar el estado"); }
  };
  const classify = async (assessment) => {
    try {
      await axios.post(`${API}/incidents/${inc.id}/classification`, { assessment, note }, { withCredentials: true });
      setNote(""); toast.success("Clasificación registrada"); onChanged();
    } catch (e) { detail(e, "Escriba una justificación (mínimo 10 caracteres)"); }
  };
  const addNote = async () => {
    try { await axios.post(`${API}/incidents/${inc.id}/notes`, { text: note }, { withCredentials: true }); setNote(""); onChanged(); }
    catch (e) { detail(e, "No se pudo guardar la observación"); }
  };
  return (
    <div className="bg-white rounded-2xl border border-gray-100 p-5 space-y-4" data-testid="incident-detail">
      <div className="flex items-start justify-between gap-2 flex-wrap">
        <div>
          <h2 className="font-bold text-gray-900">{inc.student_name} · grado {inc.grade}</h2>
          <p className="text-xs text-gray-500">#{inc.id} · registrada {fmt(inc.created_at)} · disponible {fmt(inc.available_at)} · {inc.subject}</p>
        </div>
        <Badge className={STATUS[inc.incident_status]?.cls} data-testid="incident-detail-status">{STATUS[inc.incident_status]?.label}</Badge>
      </div>
      <div className="flex flex-wrap gap-2 text-xs">
        <Badge variant="outline">{CATEGORY[inc.category] || inc.category}</Badge>
        {inc.situation && <Badge variant="outline">{SITUATION[inc.situation] || inc.situation}</Badge>}
        <Badge variant="outline">Prioridad orientativa: {inc.severity}</Badge>
        <Badge variant="outline">{inc.source === "server_detector" ? "Detección automática" : "Señalada por el tutor"}</Badge>
        <Badge variant="outline">{inc.human_reviewed_at ? `Revisada por ${inc.human_reviewed_by_name} · ${fmt(inc.human_reviewed_at)}` : "Sin revisión humana"}</Badge>
        {inc.classification_review && <Badge variant="outline" data-testid="incident-classification-badge">Clasificación: {inc.classification_review.assessment === "falso_positivo" ? "falso positivo" : "confirmada"} ({inc.classification_review.by_name})</Badge>}
      </div>
      {inc.snippet && <p className="text-sm text-gray-600 italic">{inc.snippet}</p>}
      <div className="rounded-xl bg-gray-50 p-3 text-sm space-y-2" data-testid="incident-original-content">
        <p className="whitespace-pre-wrap"><b>Mensaje del estudiante:</b> {inc.student_message || "—"}</p>
        <p className="whitespace-pre-wrap text-gray-700"><b>Respuesta del tutor:</b> {inc.tutor_response || "—"}</p>
      </div>
      <div>
        <h3 className="font-semibold text-sm text-gray-900 mb-2">Seguimiento</h3>
        <ul className="space-y-2 text-sm" data-testid="incident-notes">
          {(inc.status_history || []).map((h, i) => <li key={`h${i}`} className="text-xs text-gray-500">{fmt(h.at)} · {h.by_name}: {STATUS[h.from]?.label} → {STATUS[h.to]?.label}</li>)}
          {(inc.notes || []).map((n) => <li key={n.id} className="rounded-lg border border-gray-100 p-2"><span className="text-xs text-gray-400">{fmt(n.at)} · {n.author_name}</span><p className="whitespace-pre-wrap">{n.text}</p></li>)}
        </ul>
      </div>
      <Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Observación de seguimiento (obligatoria para cerrar)" data-testid="incident-note-input" />
      <div className="flex flex-wrap gap-2">
        <Button variant="outline" onClick={addNote} disabled={note.trim().length < 3} data-testid="incident-add-note-btn">Guardar observación</Button>
        <Button variant="outline" onClick={() => classify("confirmada")} disabled={note.trim().length < 10} data-testid="incident-classify-confirmed-btn">Clasificación correcta</Button>
        <Button variant="outline" onClick={() => classify("falso_positivo")} disabled={note.trim().length < 10} data-testid="incident-classify-false-positive-btn">Marcar falso positivo</Button>
        {(NEXT[inc.incident_status] || []).map((s) => (
          <Button key={s} onClick={() => setStatus(s)} data-testid={`incident-status-${s}-btn`}>Pasar a «{STATUS[s].label}»</Button>
        ))}
      </div>
    </div>
  );
};

export default function SensitiveIncidents() {
  const navigate = useNavigate();
  const [items, setItems] = useState(null);
  const [current, setCurrent] = useState(null);
  const [filter, setFilter] = useState("");
  const [paused, setPaused] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/incidents`, { params: filter ? { status: filter } : {}, withCredentials: true });
      setItems(data.incidents || []);
      setPaused("");
    } catch (e) {
      if (e.response?.status === 403) { toast.error("No tiene acceso a la bandeja de incidencias."); navigate("/teacher"); }
      else if (e.response?.status === 503) { setItems([]); setCurrent(null); setPaused(e.response.data?.detail || "Bandeja en pausa."); }
      else detail(e, "Error al cargar");
    }
  }, [filter, navigate]);
  const open = useCallback(async (id) => {
    try { const { data } = await axios.get(`${API}/incidents/${id}`, { withCredentials: true }); setCurrent(data); }
    catch (e) {
      if (e.response?.status === 403) { setItems([]); setCurrent(null); toast.error("Ya no tiene acceso a la bandeja de incidencias."); navigate("/teacher"); }
      else detail(e, "No se pudo abrir");
    }
  }, [navigate]);
  useEffect(() => { load(); }, [load]);
  const refresh = () => { load(); if (current) open(current.id); };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="sensitive-incidents-page">
      <header className="bg-gray-900 text-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-5 flex items-center gap-3">
          <Button variant="ghost" size="icon" className="text-white/80 hover:bg-white/10" onClick={() => navigate("/teacher")} data-testid="incidents-back-btn"><ArrowLeft className="w-5 h-5" /></Button>
          <ShieldAlert className="w-5 h-5 text-red-400" />
          <div className="flex-1">
            <h1 className="text-xl font-bold font-['Outfit']">Incidencias sensibles</h1>
            <p className="text-xs text-white/60 flex items-center gap-1"><Lock className="w-3 h-3" />Bandeja privada del responsable designado. Información confidencial; cada consulta queda registrada.</p>
          </div>
        </div>
      </header>
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-6 grid lg:grid-cols-[360px_1fr] gap-6">
        <section className="space-y-3">
          {paused && <p className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800" data-testid="incidents-paused-notice">{paused}</p>}
          {!paused && <div className="flex flex-wrap gap-1" data-testid="incidents-filter">
            {[["", "Todas"], ...Object.entries(STATUS).map(([k, v]) => [k, v.label])].map(([k, l]) => (
              <Button key={k || "all"} size="sm" variant={filter === k ? "default" : "outline"} onClick={() => setFilter(k)} data-testid={`incidents-filter-${k || "all"}`}>{l}</Button>
            ))}
          </div>}
          {paused ? null : items === null ? <p className="text-sm text-gray-400">Cargando…</p> : items.length === 0 ? (
            <p className="text-sm text-gray-400 py-8 text-center" data-testid="incidents-empty">Sin incidencias en esta vista.</p>
          ) : items.map((i) => <IncidentRow key={i.id} inc={i} active={current?.id === i.id} onOpen={open} />)}
        </section>
        <section>
          {current ? <Detail inc={current} onChanged={refresh} /> : (
            <div className="rounded-2xl border border-dashed border-gray-200 p-10 text-center text-sm text-gray-400"><Eye className="w-6 h-6 mx-auto mb-2 opacity-40" />Seleccione una incidencia para revisarla.</div>
          )}
          <p className="text-[11px] text-gray-400 mt-4">QHUB.BUDDY registra incidencias y facilita su revisión. No sustituye al profesional responsable ni a las autoridades, no ofrece vigilancia permanente ni avisa automáticamente a familias o autoridades. Líneas oficiales: CONAPINA 119 · SEM 132 · Emergencias 911.</p>
        </section>
      </main>
    </div>
  );
}
